import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import { YieldFetcher } from '../../types'

/**
 * BitFi intrinsic APR — computed on-chain, because nothing else can.
 *
 * There is no API (`api-pub.bitfi.one` answers `{"status":false,"msg":
 * "Unauthorized"}` on every public route), no DefiLlama yields pool for any
 * BitFi product, and no way to read a past share price: public Ethereum RPCs
 * refuse both archival `eth_call` and a log window wide enough to reach the
 * previous epoch. What makes this work anyway is that BOTH products keep their
 * own history on-chain — `Bfbtc.ratio(epoch)` and
 * `StakedBitFiStablecoin.epochRatios(epoch)`.
 *
 * The method is BitFi's own, from `developer/epoch-and-ratio/apy-calculation`:
 * a 7-epoch linearised annualisation, `100 · S_year · (R_N/R_{N-7} − 1) /
 * (T_N − T_{N-7})`. Reported as nominal APR, the package convention.
 *
 * **The vaults carry their own clock and bfBTC does not.** `epochRatios`
 * returns `(startRatio, endRatio, startTime, endTime)`, so hbfUSD and pbfUSD
 * are exact with no timestamp inference at all. `ratio(epoch)` returns only a
 * number, so bfBTC's elapsed time is taken from the vault's clock — which is
 * sound because the two are pushed in the SAME multisig batch: bfBTC's epoch
 * 416 landed at 1788858683 against the vaults' epoch-281 `startTime` of
 * 1788858731, 48 seconds apart. An epoch is ~89,580 s, NOT a day, and assuming
 * one would overstate every rate here by ~3.7 %.
 *
 * Two traps this encodes:
 *  - `ratio(currentEpoch)` is **0** — the live epoch is not written until it
 *    settles, so the newest readable entry is `currentEpoch − 1`. Reading the
 *    live index gives a division by zero, not a fresh number.
 *  - the epoch INDEX is chain-local (417 on Ethereum, 444 on BNB, 76 on Pharos
 *    for the identical ratio), so every chain resolves its own `currentEpoch`
 *    and no index is ever carried across chains. The VALUES are shared on the
 *    synced chains and stale on the small ones — TAC and GOAT sat 3.9 % behind
 *    at integration — which is exactly why each row is measured on its own
 *    deployment rather than inheriting Ethereum's answer.
 */

const RATIO_ABI = [
  {
    name: 'ratio',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'currentEpoch',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

const EPOCH_RATIOS_ABI = [
  {
    name: 'epochRatios',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256' }],
    outputs: [
      { type: 'uint64', name: 'startRatio' },
      { type: 'uint64', name: 'endRatio' },
      { type: 'uint64', name: 'startTime' },
      { type: 'uint64', name: 'endTime' },
    ],
  },
] as const

/** Window the APR is measured over, in epochs — BitFi's own documented one. */
const WINDOW = 7

const SECONDS_PER_YEAR = 31_536_000

/**
 * Anchor for locating the vaults' current epoch, which has no getter.
 *
 * Measured 2026-09-08 on both hbfUSD and pbfUSD (they share an index and a
 * schedule). Only used to centre a ±6 probe window, so drift costs nothing
 * until it exceeds six epochs — about six days — and the probe still finds the
 * right one as long as the estimate lands inside the window.
 */
const VAULT_EPOCH_ANCHOR = { epoch: 281, startTime: 1_788_858_731 }

/**
 * Nominal seconds per epoch — measured, not assumed: epochs start ~89,580 s
 * apart (a 23 h vesting window plus a settlement gap). Used to centre the
 * probe, and as the last-resort divisor if the vault clock is unreadable.
 */
const NOMINAL_EPOCH_SECONDS = 89_580

/** hbfUSD / pbfUSD, Ethereum. */
const BFUSD_VAULTS: { key: string; address: string }[] = [
  { key: 'hbfUSD', address: '0xde5d4ab42251ba9af6f247cf07c9a4793fa6ed88' },
  { key: 'pbfUSD', address: '0x4f85cbfdefbdb3fa96fcf1e38b0ee68db9ae438b' },
]

/** bfBTC deployments this fetcher measures, one entry per registry row. */
const BFBTC_DEPLOYMENTS: { chain: string; address: string }[] = [
  {
    chain: Chain.ETHEREUM_MAINNET,
    address: '0xcdfb58c8c859cb3f62ebe9cf2767f9e036c7fb15',
  },
  {
    chain: Chain.BNB_SMART_CHAIN_MAINNET,
    address: '0x623f2774d9f27b59bc6b954544487532ce79d9df',
  },
  { chain: Chain.BASE, address: '0x623f2774d9f27b59bc6b954544487532ce79d9df' },
  {
    chain: Chain.HEMI_NETWORK,
    address: '0x623f2774d9f27b59bc6b954544487532ce79d9df',
  },
  {
    chain: Chain.BITLAYER_MAINNET,
    address: '0xcdfb58c8c859cb3f62ebe9cf2767f9e036c7fb15',
  },
  {
    chain: Chain.PHAROS_MAINNET,
    address: '0x623f2774d9f27b59bc6b954544487532ce79d9df',
  },
]

/** Result key — chain-scoped, since one bfBTC address serves six chains and
 *  their ratios are allowed to diverge. */
export const bitfiYieldKey = (chainId: string, address: string): string =>
  `bitfi::${chainId}::${address.toLowerCase()}`

/** An APR outside this band is a broken read, not a strategy result. The
 *  floor is negative on purpose: bfBTC's ratio has fallen in 6 of 416 epochs
 *  and a delta-neutral book can genuinely lose a week. */
const plausible = (apr: number): boolean =>
  Number.isFinite(apr) && apr > -50 && apr < 100

type EpochRow = {
  startRatio: bigint
  endRatio: bigint
  startTime: bigint
  endTime: bigint
}

const asEpochRow = (cell: unknown): EpochRow | undefined => {
  if (!Array.isArray(cell) || cell.length < 4) return undefined
  const [a, b, c, d] = cell
  if (
    typeof a !== 'bigint' ||
    typeof b !== 'bigint' ||
    typeof c !== 'bigint' ||
    typeof d !== 'bigint'
  )
    return undefined
  return { startRatio: a, endRatio: b, startTime: c, endTime: d }
}

/**
 * Read both bfUSD vaults' epoch ledgers around the estimated current epoch.
 *
 * Returns each vault's APR plus the protocol's seconds-per-epoch, which bfBTC
 * then borrows. A vault whose window cannot be resolved contributes nothing
 * rather than a guessed rate.
 */
const readVaults = async (): Promise<{
  aprs: Record<string, number>
  secondsPerEpoch?: number
}> => {
  const estimate =
    VAULT_EPOCH_ANCHOR.epoch +
    Math.floor(
      (Date.now() / 1000 - VAULT_EPOCH_ANCHOR.startTime) /
        NOMINAL_EPOCH_SECONDS,
    )
  // Probe a window around the estimate, plus the same window shifted back by
  // WINDOW epochs — one multicall covers both ends of the measurement.
  const probe: number[] = []
  for (let e = estimate - 6; e <= estimate + 6; e++) {
    if (e - WINDOW >= 0) probe.push(e - WINDOW)
    if (e >= 0) probe.push(e)
  }
  const epochs = [...new Set(probe)].sort((a, b) => a - b)

  const results = (await multicallRetryUniversal({
    chain: Chain.ETHEREUM_MAINNET,
    abi: EPOCH_RATIOS_ABI,
    calls: BFUSD_VAULTS.flatMap(({ address }) =>
      epochs.map((e) => ({
        address,
        name: 'epochRatios',
        params: [BigInt(e)],
      })),
    ),
    allowFailure: true,
  })) as unknown[]

  const aprs: Record<string, number> = {}
  const cadences: number[] = []

  BFUSD_VAULTS.forEach(({ address }, vi) => {
    const rows = new Map<number, EpochRow>()
    epochs.forEach((e, i) => {
      const row = asEpochRow(results[vi * epochs.length + i])
      // `startTime === 0` is an epoch that does not exist yet, not epoch zero
      // of the unix clock — the probe deliberately reaches past the end.
      if (row && row.startTime > 0n && row.startRatio > 0n) rows.set(e, row)
    })
    const latest = Math.max(...[...rows.keys()], -1)
    if (latest < 0) return
    const now = rows.get(latest)
    const then = rows.get(latest - WINDOW)
    if (!now || !then) return

    const elapsed = Number(now.startTime - then.startTime)
    if (elapsed <= 0) return
    cadences.push(elapsed / WINDOW)

    const growth = Number(now.startRatio) / Number(then.startRatio) - 1
    const apr = (100 * SECONDS_PER_YEAR * growth) / elapsed
    if (plausible(apr)) {
      aprs[bitfiYieldKey(Chain.ETHEREUM_MAINNET, address)] = apr
    }
  })

  const secondsPerEpoch = cadences.length
    ? cadences.reduce((a, b) => a + b, 0) / cadences.length
    : undefined
  // A clock outside this band means the probe latched onto the wrong epochs;
  // fall back rather than annualise with a nonsense divisor.
  const usable =
    secondsPerEpoch !== undefined &&
    secondsPerEpoch > 43_200 &&
    secondsPerEpoch < 259_200
  return { aprs, secondsPerEpoch: usable ? secondsPerEpoch : undefined }
}

/** bfBTC on one chain: `currentEpoch()`, then the two ratios around it. */
const readBfbtc = async (
  chain: string,
  address: string,
  secondsPerEpoch: number,
): Promise<Record<string, number>> => {
  const [epochRaw] = (await multicallRetryUniversal({
    chain,
    abi: RATIO_ABI,
    calls: [{ address, name: 'currentEpoch', params: [] }],
    allowFailure: true,
  })) as unknown[]
  if (typeof epochRaw !== 'bigint') return {}
  // The live epoch is not written until it settles, so the newest readable
  // entry is one behind.
  const latest = Number(epochRaw) - 1
  if (latest - WINDOW < 0) return {}

  const [nowRaw, thenRaw] = (await multicallRetryUniversal({
    chain,
    abi: RATIO_ABI,
    calls: [
      { address, name: 'ratio', params: [BigInt(latest)] },
      { address, name: 'ratio', params: [BigInt(latest - WINDOW)] },
    ],
    allowFailure: true,
  })) as unknown[]
  if (typeof nowRaw !== 'bigint' || typeof thenRaw !== 'bigint') return {}
  if (nowRaw <= 0n || thenRaw <= 0n) return {}

  // The ratio is bfBTC-per-BTC, so a holder's rate is its reciprocal and the
  // growth term inverts with it.
  const growth = Number(thenRaw) / Number(nowRaw) - 1
  const apr = (100 * SECONDS_PER_YEAR * growth) / (WINDOW * secondsPerEpoch)
  return plausible(apr) ? { [bitfiYieldKey(chain, address)]: apr } : {}
}

export const bitfiFetcher: YieldFetcher = {
  label: 'BITFI',
  fetch: async () => {
    const { aprs, secondsPerEpoch } = await readVaults()
    const clock = secondsPerEpoch ?? NOMINAL_EPOCH_SECONDS

    const perChain = await Promise.all(
      BFBTC_DEPLOYMENTS.map(({ chain, address }) =>
        readBfbtc(chain, address, clock).catch(() => ({})),
      ),
    )

    const out: Record<string, number> = { ...aprs }
    for (const part of perChain) Object.assign(out, part)

    if (Object.keys(out).length === 0) {
      throw new Error('BitFi: no product returned a readable epoch ratio')
    }
    return out
  },
}
